import clsx from 'clsx';
import CountryCode from 'components/atoms/TextInput/CountryCode';
import TranslationWrapper from 'components/templates/Translation/TranslationWrapper';
import usePanelConfiguration from 'hooks/usePanelConfiguration';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { EMAIL_REGEX, PHONENUMBER_REGEX, USERNAME_REGEX } from 'utils/formHelper';
import TextInput, { Props as TextInputProps } from './TextInput';
import classes from './UsernameInput.module.scss';

export enum UsernameInputMode {
  EMAIL = 1,
  PHONE = 2,
}

export type Props = {
  forceMode?: UsernameInputMode;
  overrideTitle?: string;
} & Omit<TextInputProps, 'id' | 'name' | 'title' | 'placeholder' | 'type'>;

export const FORM_KEY = 'username';
const namespace = 'common';

function UsernameInput(
  { forceMode, overrideTitle, ...rest }: Props,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const panelConfiguration = usePanelConfiguration();

  const [inputMode, setInputMode] = useState<UsernameInputMode | null>(
    forceMode || UsernameInputMode.EMAIL,
  );
  const strings = useMemo(() => {
    if (inputMode === UsernameInputMode.EMAIL) {
      return {
        title: overrideTitle || 'Email Address',
        placeholder: 'Enter Email Address',
        autoComplete: 'username email',
        pattern: EMAIL_REGEX.source,
        errorMessage: 'Please enter a valid email address',
      };
    } else if (inputMode === UsernameInputMode.PHONE) {
      return {
        title: overrideTitle || 'Mobile Number',
        placeholder: 'Enter Mobile Number',
        autoComplete: 'username tel',
        pattern: PHONENUMBER_REGEX.source,
        errorMessage: 'Please enter a valid mobile number',
      };
    } else {
      return {
        title: overrideTitle || 'Email Address or Mobile Number',
        placeholder: 'Enter Email Address or Mobile Number',
        autoComplete: 'username email tel',
        pattern: USERNAME_REGEX.source,
        errorMessage: 'Please enter a valid email address or mobile number',
      };
    }
  }, [inputMode, overrideTitle]);

  useEffect(() => {
    if (!forceMode && panelConfiguration) {
      setInputMode((input) => {
        let mode = 0;
        if (panelConfiguration.registrationOptions.email) {
          mode |= UsernameInputMode.EMAIL;
        }
        if (panelConfiguration.registrationOptions.phoneNumber) {
          mode |= UsernameInputMode.PHONE;
        }
        return mode;
      });
    }
  }, [panelConfiguration, forceMode]);

  return (
    <TranslationWrapper namespace={namespace}>
      <TextInput
        id={FORM_KEY}
        name={FORM_KEY}
        title={strings.title}
        placeholder={strings.placeholder}
        type="text"
        required
        autoComplete={strings.autoComplete}
        pattern={strings.pattern}
        overrideErrorMessage={() => {
          return strings.errorMessage;
        }}
        startAdornment={
          inputMode === UsernameInputMode.PHONE && (
            <CountryCode
              alpha2Code={panelConfiguration?.countryDetails.alpha2 || ''}
              alpha3Code={panelConfiguration?.countryDetails.alpha3 || ''}
              phoneCode={panelConfiguration?.countryDetails.phoneNumberPrefixes[0] || 0}
            />
          )
        }
        startAdornmentClassName={classes.startAdornment}
        inputClassName={clsx(inputMode === UsernameInputMode.PHONE && classes.inputWithAdornment)}
        labelClassName={clsx(inputMode === UsernameInputMode.PHONE && classes.labelWithAdornment)}
        ref={ref}
        {...rest}
      />
    </TranslationWrapper>
  );
}

const ForwardedUsernameInput = React.forwardRef<HTMLInputElement, Props>(UsernameInput);

/**
 * React behaves strangely when one of a set of sibling input elements is suspended. We wrap this in
 * its own Suspense boundary with a fallback to a <div> which is the same wrapper element used by
 * TextInput so when UsernameInput stops being suspended its tree will render cleanly into the same div.
 * @see https://legacy.reactjs.org/docs/reconciliation.html
 */
const SuspendingUsernameInput = (props: Props, ref: React.ForwardedRef<HTMLInputElement>) => {
  return (
    <Suspense fallback={<div hidden={true}></div>}>
      <ForwardedUsernameInput ref={ref} {...props} />
    </Suspense>
  );
};

const ForwardedSuspendingUsernameInput = React.forwardRef<HTMLInputElement, Props>(
  SuspendingUsernameInput,
);

export default ForwardedSuspendingUsernameInput;
